import { useCallback, useEffect, useState } from "react";
import { HeartPulse, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import { useLiveRefresh } from "@/lib/live";
import { CardBody, FamilyBody, SevereBadge } from "@/components/emergency/parts";
import { CardEditor, FamilyEditor } from "@/components/emergency/Editors";
import SitterLinks from "@/components/emergency/SitterLinks";
import HouseInfo, { PrintCard } from "@/components/emergency/House";
import CardDocs from "@/components/emergency/CardDocs";
import { writesLocked } from "@/lib/splits";

const CYAN = "var(--glow-family)";

export default function Emergency() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [print, setPrint] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/emergency`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => (m.type || "").startsWith("emergency."));
  if (!d) return <Loader />;
  return (
    <div data-testid="emergency-page">
      <PageHeader eyebrow="Family" title="Emergency info" rgb={CYAN} testid="emergency-title" />
      {d.is_parent && <button className="q-btn q-btn-secondary mb-4 min-h-[44px]" onClick={() => setPrint(true)} data-testid="print-card-btn"><Printer className="h-4 w-4" /> Print card</button>}
      {d.severe.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border p-3" style={{ borderColor: "rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)" }} data-testid="severe-banner">
          <SevereBadge testid="severe-banner-badge" />
          {d.severe.map((s) => <span key={s.key} className="text-sm font-bold text-q-text" data-testid={`severe-person-${s.name}`}>{s.name}: {s.allergens.join(", ")}</span>)}
        </div>
      )}
      <section className="q-card mb-4" data-testid="family-card">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex-1 font-display text-lg font-extrabold text-q-text">Family</h2>
          {d.can_edit_family && <button className="q-btn q-btn-ghost" onClick={() => setEdit({ family: true })} data-testid="family-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>}
        </div>
        <FamilyBody family={d.family} />
      </section>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {d.members.map((m) => (
          <section key={m.key} className="q-card min-w-0" data-testid={`em-card-${m.name.split(" ")[0]}`}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <HeartPulse className="h-5 w-5 text-[rgb(var(--glow-family))]" />
              <h3 className="min-w-0 flex-1 truncate font-display font-extrabold text-q-text">{m.name}</h3>
              {m.card.severe.length > 0 && <SevereBadge testid={`severe-badge-${m.name.split(" ")[0]}`} />}
              {m.can_edit && <button className="q-btn q-btn-ghost w-11 px-0" aria-label={`Edit ${m.name}`} onClick={() => setEdit({ member: m })} data-testid={`em-edit-${m.name.split(" ")[0]}`}><Pencil className="h-4 w-4" /></button>}
            </div>
            <CardBody card={m.card} keyId={m.name.split(" ")[0]} />
            {hub.my_role !== "kid" && <CardDocs hubId={hub.id} member={m} locked={writesLocked(hub)} />}
          </section>
        ))}
      </div>
      {d.is_parent && <HouseInfo hubId={hub.id} readOnly={writesLocked(hub)} />}
      {d.is_parent && <div className="mt-6"><SitterLinks hubId={hub.id} kids={d.members.filter((m) => m.kind === "kid")} readOnly={writesLocked(hub)} /></div>}
      {print && <PrintCard hubId={hub.id} members={d.members} onClose={() => setPrint(false)} />}
      {edit?.member && <CardEditor hubId={hub.id} member={edit.member} bloodTypes={d.blood_types} onClose={() => setEdit(null)} onSaved={load} />}
      {edit?.family && <FamilyEditor hubId={hub.id} family={d.family} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
